import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Heart, Sparkles, TrendingUp, Users, ShoppingBag, GraduationCap, IndianRupee } from "lucide-react";

export default function Impact() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/impact/stats").then((r) => setStats(r.data)); }, []);

  const fmt = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      {/* Hero */}
      <div className="rounded-3xl p-10 lg:p-16 bg-gradient-to-br from-brand-green to-emerald-700 text-white text-center mb-14">
        <Heart size={40} className="mx-auto text-white/90 mb-4" />
        <div className="text-sm uppercase tracking-[0.3em] text-white/80 mb-3">Our promise</div>
        <h1 data-testid="impact-heading" className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight">
          Learning for All.<br />
          <span className="text-brand-orange">Bridging the gap of inequality.</span>
        </h1>
        <p className="mt-6 text-lg text-white/90 max-w-2xl mx-auto">
          15% of every commission we earn from the FuturePath marketplace goes into our "Learning for All"
          impact pool — funding books, learning kits, and mentors for children in orphanages across India.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid md:grid-cols-4 gap-5 mb-14">
          <div className="surface p-6"><ShoppingBag className="text-brand-green mb-3" /><div className="text-3xl font-heading font-bold text-brand-blue">{fmt(stats.clicks_total)}</div><div className="text-sm text-brand-muted">Marketplace clicks</div></div>
          <div className="surface p-6"><TrendingUp className="text-brand-orange mb-3" /><div className="text-3xl font-heading font-bold text-brand-blue">₹{fmt(stats.estimated_gmv_inr)}</div><div className="text-sm text-brand-muted">Est. purchase volume</div></div>
          <div className="surface p-6"><IndianRupee className="text-brand-blue mb-3" /><div className="text-3xl font-heading font-bold text-brand-blue">₹{fmt(stats.estimated_commission_inr)}</div><div className="text-sm text-brand-muted">Est. commission earned</div></div>
          <div className="surface p-6 bg-gradient-to-br from-brand-green-soft to-white border-brand-green/30"><Heart className="text-brand-green mb-3" /><div className="text-3xl font-heading font-bold text-brand-green">₹{fmt(stats.estimated_donation_inr)}</div><div className="text-sm text-brand-muted">Committed to donations</div></div>
        </div>
      )}

      {/* How it works */}
      <div className="mb-14">
        <div className="overline mb-3">How Learning for All works</div>
        <h2 className="text-3xl sm:text-4xl font-semibold text-brand-blue">Every click. Every book. Every kid.</h2>
        <div className="mt-8 grid md:grid-cols-4 gap-5">
          {[
            { icon: ShoppingBag, n: "01", t: "You shop", d: "You buy a product for your child from our curated marketplace via our Amazon affiliate link." },
            { icon: TrendingUp, n: "02", t: "Amazon pays us", d: "Amazon pays FuturePath a small affiliate commission (typically 1-9% depending on category)." },
            { icon: Heart, n: "03", t: "15% is set aside", d: "We automatically earmark 15% of every commission into the Learning for All impact pool." },
            { icon: GraduationCap, n: "04", t: "A child learns", d: "We use the pool to fund books, learning kits, and mentors for orphanages — accounted publicly on this page." },
          ].map((s) => (
            <div key={s.n} className="surface p-6">
              <div className="text-brand-orange font-heading font-bold text-sm mb-2">{s.n}</div>
              <s.icon size={22} className="text-brand-green mb-3" />
              <h3 className="font-heading font-semibold text-brand-blue">{s.t}</h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Transparency */}
      <div className="surface p-8 mb-14">
        <Sparkles className="text-brand-orange mb-3" />
        <h2 className="text-2xl font-semibold text-brand-blue">Transparency by default.</h2>
        <p className="mt-3 text-brand-muted leading-relaxed">
          We publish quarterly impact updates: total commission earned, exact rupees disbursed, and the orphanages / learning programmes supported.
          Every family who shops with us can see exactly how much their choices contributed. When our first NGO partnership is finalised, it will be
          named right here on this page — with signed MoUs and 80G-eligible receipts where possible.
        </p>
      </div>

      {/* CTA */}
      <div className="text-center">
        <h3 className="text-2xl font-semibold text-brand-blue">Shop with a purpose.</h3>
        <p className="mt-2 text-brand-muted">Curated products, age-matched, and every click gives back.</p>
        <Link to="/marketplace" className="btn-primary mt-6 inline-flex"><ShoppingBag size={14} /> Explore marketplace</Link>
      </div>
    </div>
  );
}
